import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  Icon,
  HStack,
  keyframes,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import type { KeyboardEvent } from "react";
import {
  FiChevronDown,
  FiShield,
  FiMapPin,
  FiBell,
  FiZap,
} from "react-icons/fi";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const gradientPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(10px); }
`;

interface HowItWorksHeroProps {
  onScrollClick: () => void;
}

export default function HowItWorksHero({ onScrollClick }: HowItWorksHeroProps) {
  const shouldReduceMotion = useReducedMotion();
  const floatAnimation = shouldReduceMotion
    ? undefined
    : `${float} 6s ease-in-out infinite`;
  const pulseAnimation = shouldReduceMotion
    ? undefined
    : `${pulse} 4s ease-in-out infinite`;
  const bounceAnimation = shouldReduceMotion
    ? undefined
    : `${bounce} 2s ease-in-out infinite`;

  return (
    <Flex
      w="full"
      minH="100vh"
      position="relative"
      overflow="hidden"
      bg="#0a0f1a"
    >
      {/* Static gradient background + compositable opacity overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgGradient="linear(135deg, #0a0f1a 0%, #1a2a4a 50%, #0a0f1a 100%)"
      />
      {!shouldReduceMotion && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgGradient="linear(135deg, #1a1f3a 0%, #0f1829 50%, #1a2a4a 100%)"
          animation={`${gradientPulse} 15s ease infinite`}
        />
      )}

      {/* Floating orbs */}
      <Box
        position="absolute"
        top="15%"
        right="15%"
        w={{ base: "150px", md: "350px" }}
        h={{ base: "150px", md: "350px" }}
        borderRadius="full"
        bg="linear-gradient(135deg, rgba(255, 79, 0, 0.25), rgba(255, 120, 50, 0.1))"
        filter="blur(80px)"
        animation={floatAnimation}
      />
      <Box
        position="absolute"
        bottom="25%"
        left="10%"
        w={{ base: "100px", md: "250px" }}
        h={{ base: "100px", md: "250px" }}
        borderRadius="full"
        bg="linear-gradient(135deg, rgba(0, 150, 255, 0.15), rgba(100, 200, 255, 0.08))"
        filter="blur(60px)"
        animation={
          shouldReduceMotion ? undefined : `${float} 8s ease-in-out infinite`
        }
      />

      {/* Grid pattern overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.03}
        backgroundImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
        backgroundSize="50px 50px"
      />

      <VStack
        w="full"
        justify="center"
        px={useBreakpointValue({ base: 6, md: 8 })}
        minH="100vh"
        position="relative"
        zIndex={1}
      >
        <Container maxW="4xl">
          <VStack spacing={{ base: 8, md: 10 }} align="center">
            {/* Badge */}
            <HStack
              bg="rgba(255, 79, 0, 0.1)"
              border="1px solid rgba(255, 79, 0, 0.3)"
              borderRadius="full"
              px={6}
              py={2}
              animation={pulseAnimation}
            >
              <Icon as={FiZap} color="brand.500" aria-hidden="true" />
              <Text
                color="brand.500"
                fontSize="sm"
                fontWeight={600}
                letterSpacing="wide"
              >
                REAL-TIME SATELLITE ALERTS
              </Text>
            </HStack>

            <Heading
              color="white"
              fontWeight={700}
              fontSize={{ base: "4xl", sm: "5xl", md: "7xl", lg: "8xl" }}
              lineHeight="1.1"
              textAlign="center"
              letterSpacing="-0.02em"
            >
              How It{" "}
              <Text
                as="span"
                bgGradient="linear(to-r, brand.500, brand.700)"
                bgClip="text"
              >
                Works
              </Text>
            </Heading>

            <Text
              color="text.on-black-secondary"
              maxW="2xl"
              textAlign="center"
              fontSize={{ base: "lg", md: "xl" }}
              lineHeight="1.8"
            >
              Providing you with safety alerts is simple. Making sure you
              receive relevant, clear, and reliable alerts before you even know
              you need them is our magic.
            </Text>

            {/* Quick info cards */}
            <HStack
              spacing={{ base: 4, md: 8 }}
              pt={6}
              flexWrap="wrap"
              justify="center"
            >
              <HStack color="text.on-black-muted" spacing={2}>
                <Icon as={FiMapPin} color="brand.500" aria-hidden="true" />
                <Text fontSize="sm">Location Tracking</Text>
              </HStack>
              <HStack color="text.on-black-muted" spacing={2}>
                <Icon as={FiShield} color="brand.500" aria-hidden="true" />
                <Text fontSize="sm">24/7 Monitoring</Text>
              </HStack>
              <HStack color="text.on-black-muted" spacing={2}>
                <Icon as={FiBell} color="brand.500" aria-hidden="true" />
                <Text fontSize="sm">Instant Alerts</Text>
              </HStack>
            </HStack>

            {/* Scroll indicator */}
            <Box
              as="button"
              pt={12}
              cursor="pointer"
              onClick={onScrollClick}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onScrollClick();
                }
              }}
              animation={bounceAnimation}
              _hover={{ color: "brand.500" }}
              transition="color 0.3s ease"
              aria-label="Scroll to learn more"
            >
              <VStack spacing={2} color="text.on-black-muted">
                <Text fontSize="sm" fontWeight={500}>
                  Learn More
                </Text>
                <Icon as={FiChevronDown} boxSize={6} aria-hidden="true" />
              </VStack>
            </Box>
          </VStack>
        </Container>
      </VStack>
    </Flex>
  );
}
